import { createContext, useContext, useEffect, useReducer, useRef, useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import Cookies from 'js-cookie';
import { jsx } from 'react/jsx-runtime';

// src/bridgeProvider.tsx

// src/types/events.ts
var EventName = /* @__PURE__ */ ((EventName2) => {
  EventName2["Connecting"] = "connecting";
  EventName2["SetConfig"] = "setConfig";
  EventName2["Handshake"] = "handshake";
  EventName2["OpenUrl"] = "openUrl";
  EventName2["OpenPayment"] = "openPayment";
  EventName2["PaymentStatus"] = "paymentStatus";
  EventName2["CloseWebview"] = "closeWebview";
  EventName2["Share"] = "share";
  return EventName2;
})(EventName || {});

// src/types/store.ts
var Store;
((Store2) => {
  ((InternalEventName2) => {
    InternalEventName2["SyncStorage"] = "syncStorage";
  })(Store2.InternalEventName || (Store2.InternalEventName = {}));
})(Store || (Store = {}));

// src/store.ts
var initialState = {
  isWebView: false,
  isConnected: false,
  isSetConfig: false,
  bridgeVersion: 0
};
var bridgeReducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case Store.InternalEventName.SyncStorage: {
      return {
        ...state,
        ...payload
      };
    }
    case "connecting" /* Connecting */: {
      return {
        ...state,
        isConnected: true
      };
    }
    case "setConfig" /* SetConfig */: {
      return {
        ...state,
        ...payload,
        isSetConfig: true
      };
    }
    case "handshake" /* Handshake */: {
      return {
        ...state,
        ...payload,
        isWebView: true
      };
    }
    default:
      return state;
  }
};

// src/utils/parser.ts
var JSON_PATTERN = /^\{.*}$/;
var parseEventMessage = (data) => {
  if (data && typeof data === "string" && JSON_PATTERN.test(data)) {
    const message = JSON.parse(data);
    if (!message.eventName) return null;
    return message;
  }
  return null;
};
var isValidOrigin = (origin) => {
  return bridgeConfig.eventOriginRule.test(origin);
};
var BridgeMessagingAPI = class _BridgeMessagingAPI {
  static get origin() {
    return window.location.origin;
  }
  static subscribe(callback) {
    window.addEventListener("message", callback, true);
    return () => window.removeEventListener("message", callback);
  }
  static sendEventToReactNative({ eventName, params }) {
    try {
      if (!window.ReactNativeWebView?.postMessage) {
        return null;
      }
      window.ReactNativeWebView.postMessage(JSON.stringify({ eventName, params }));
    } catch (error) {
      console.log(error);
      throw new Error(`Something went wrong during ${eventName} execution`);
    }
  }
  static connecting() {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "connecting" /* Connecting */,
      params: { origin: _BridgeMessagingAPI.origin }
    });
  }
  static handshake() {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "handshake" /* Handshake */,
      params: { origin: _BridgeMessagingAPI.origin }
    });
  }
  static closeWebview() {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "closeWebview" /* CloseWebview */,
      params: { origin: _BridgeMessagingAPI.origin }
    });
    if (bridgeConfig) {
      localStorage.removeItem(bridgeConfig.storageKey);
      Cookies.remove(bridgeConfig.cookieSessionIdKey);
    }
  }
  static setConfig(config) {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "setConfig" /* SetConfig */,
      params: { ...config, origin: _BridgeMessagingAPI.origin }
    });
  }
  static openUrl(url) {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "openUrl" /* OpenUrl */,
      params: { url, origin: _BridgeMessagingAPI.origin }
    });
  }
  static openPayment(url) {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "openPayment" /* OpenPayment */,
      params: { url, origin: _BridgeMessagingAPI.origin }
    });
  }
  static share(params) {
    _BridgeMessagingAPI.sendEventToReactNative({
      eventName: "share" /* Share */,
      params: { ...params, origin: _BridgeMessagingAPI.origin }
    });
  }
};
var BridgeContext = createContext(initialState);
var defaultAPI = {
  closeWebview: BridgeMessagingAPI.closeWebview,
  openUrl: BridgeMessagingAPI.openUrl,
  openPayment: BridgeMessagingAPI.openPayment,
  share: BridgeMessagingAPI.share
};
var BridgeAPIContext = createContext(defaultAPI);
var BridgeProvider = ({ children, isServerWebView, config }) => {
  const [state, dispatch] = useReducer(bridgeReducer, {
    ...initialState,
    isWebView: isServerWebView
  });
  const [storage, setStorage] = useLocalStorage(config.storageKey, null);
  const cookieSessionId = Cookies.get(config.cookieSessionIdKey);
  const sessionId = useRef(cookieSessionId ? Number(cookieSessionId) : null);
  useEffect(() => {
    const currentSessionId = sessionId.current;
    let payload = { ...initialState, isWebView: isServerWebView };
    if (storage && currentSessionId) {
      if (storage.sessionId === currentSessionId) {
        payload = storage;
      } else {
        setStorage(null);
      }
    }
    dispatch({ type: Store.InternalEventName.SyncStorage, payload });
  }, []);
  useEffect(() => {
    if (storage) return;
    if (state.isSetConfig && state.isConnected) {
      BridgeMessagingAPI.handshake();
    } else if (state.isConnected) {
      const _config = {
        originBrowserList: config.originBrowserList
      };
      BridgeMessagingAPI.setConfig(_config);
    } else {
      BridgeMessagingAPI.connecting();
    }
  }, [storage, state]);
  const onHandshake = useCallback(() => {
    if (sessionId.current) {
      state.sessionId = sessionId.current;
    }
    state.isWebView = true;
    setStorage(state);
  }, [state]);
  useEffect(() => {
    const callback = (nativeEvent) => {
      try {
        const message = nativeEvent.data;
        const data = parseEventMessage(message);
        if (!data || !data.params.origin) return;
        const { origin, ...payload } = data.params;
        if (!isValidOrigin(origin)) return;
        if (data.eventName === "handshake" /* Handshake */) onHandshake();
        dispatch({
          type: data.eventName,
          payload
        });
      } catch (error) {
        console.log(error);
      }
    };
    return BridgeMessagingAPI.subscribe(callback);
  }, [onHandshake]);
  return /* @__PURE__ */ jsx(BridgeContext.Provider, { value: state, children: /* @__PURE__ */ jsx(BridgeAPIContext.Provider, { value: defaultAPI, children }) });
};

// src/config.ts
var Config = class {
  /**
   * The bridge configuration object.
   * @type {BridgeConfig}
   * @protected
   */
  config;
  /**
   * Default list of origin URLs to be opened in the native browser.
   * @type {string[]}
   * @protected
   */
  defaultOriginBrowserList = [
    "https://www.instagram.com",
    "https://www.facebook.com",
    "https://maps.google.com",
    "https://www.youtube.com",
    "https://youtu.be"
  ];
  /**
   * Key used to indicate that the top safe area should not be applied.
   * @type {string}
   * @static
   * @readonly
   */
  static noTopSafeAreaKey = "top-safearea";
  /**
   * Creates an instance of ProtectedConfig.
   * @param {BridgeConfig} config - The bridge configuration.
   */
  constructor(config) {
    this.config = config;
  }
  /**
   * Gets the mini app name.
   * @returns {string} The name of the mini app.
   */
  get name() {
    return this.config.miniapp;
  }
  /**
   * Gets the list of origin URLs that should be opened in the native browser.
   * @returns {string[]} The list of origin URLs.
   */
  get originBrowserList() {
    return [...this.defaultOriginBrowserList, ...this.config.originBrowserList || []];
  }
  /**
   * Gets the list of routes where the top safe area should be disabled.
   * @returns {RegExp[]} The list of route patterns.
   */
  get routesReqWithoutTopSafeAre() {
    return this.config.routesHideTopSafeAre ?? [];
  }
  /**
   * Gets the cookie key for the session ID.
   * @returns {string} The cookie key for the session ID.
   */
  get cookieSessionIdKey() {
    return `_${this.config.miniapp}_bridge_session_id`;
  }
  /**
   * Gets the key used for storage.
   * @returns {string} The storage key.
   */
  get storageKey() {
    return `_${this.config.miniapp}_bridge_storage`;
  }
  /**
   * Gets the origin used for validating incoming events.
   * @returns {RegExp} The origin regular expression.
   */
  get eventOriginRule() {
    return this.config.eventOriginRule;
  }
};
var bridgeConfig;
var Bridge = ({ children, isServerWebView, config }) => {
  const initialConfig = new Config(config);
  bridgeConfig = initialConfig;
  return /* @__PURE__ */ jsx(BridgeProvider, { isServerWebView, config: initialConfig, children });
};
var useBridgeState = () => {
  return useContext(BridgeContext);
};
var useBridgeAPI = () => {
  return useContext(BridgeAPIContext);
};
var withBridgeState = (Component) => {
  const BridgeStateWrapper = (props) => {
    const state = useBridgeState();
    return /* @__PURE__ */ jsx(Component, { ...props, bridgeState: state });
  };
  BridgeStateWrapper.displayName = `withBridgeState(${Component.displayName || "BridgeStateWrapper"})`;
  return BridgeStateWrapper;
};
var withBridgeAPI = (Component) => {
  const BridgeAPIWrapper = (props) => {
    const state = useBridgeAPI();
    return /* @__PURE__ */ jsx(Component, { ...props, bridgeAPI: state });
  };
  BridgeAPIWrapper.displayName = `withBridgeAPI(${Component.displayName || "BridgeAPIWrapper"})`;
  return BridgeAPIWrapper;
};

// src/utils/noTopSafeArea.ts
var noTopSafeArea = (pathname = "", replace) => {
  if (typeof window !== "undefined") {
    const parsedUrl = pathname ? new URL(`${window.location.origin}${pathname}`) : new URL(`${window.location.href}`);
    const querySafeAreaExist = parsedUrl.searchParams.get(Config.noTopSafeAreaKey) === "false";
    if (bridgeConfig.routesReqWithoutTopSafeAre.some((rx) => rx.test(parsedUrl.pathname)) && !querySafeAreaExist) {
      parsedUrl.searchParams.set(Config.noTopSafeAreaKey, "false");
      replace(parsedUrl.toString()).catch(console.warn);
    }
  }
};

// src/hooks/listener.tsx
var useWebViewPostMessage = ({
  eventName,
  handler
}) => {
  useEffect(() => {
    const handleEvent = (nativeEvent) => {
      try {
        const data = parseEventMessage(nativeEvent.data);
        if (data && data.eventName === eventName) {
          handler(data.params);
        }
      } catch (e) {
        console.error(e);
      }
    };
    window.addEventListener("message", handleEvent, true);
    return () => window.removeEventListener("message", handleEvent, true);
  }, [eventName, handler]);
  return null;
};

export { Bridge, Config, EventName, Store, bridgeConfig, isValidOrigin, noTopSafeArea, parseEventMessage, useBridgeAPI, useBridgeState, useWebViewPostMessage, withBridgeAPI, withBridgeState };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map